/**
 * Phase-events store (Crucible #416 Chunk C).
 *
 * In-memory, ephemeral, per-session buffer of `PhaseObservableEvent`
 * mirrors. The renderer-side click-to-expand panel needs to filter
 * the chat log by `event.phaseSeq === clickedPhase.seq`; this store is
 * the renderer's mirror of the SKILL-side `recordPhaseObservableEvent`
 * buffer.
 *
 * Strictly NOT persisted — these excerpts are session-scoped per the
 * v10 spec ("ephemeral, in-session"). Discarded on app reload.
 *
 * The chat reducer pushes events here as they stream in. The current
 * `phaseSeq` is whatever was last pushed via `setCurrentPhaseSeq()`,
 * which the `PhaseStrip` does whenever a `derived` update arrives
 * with a non-empty `transitions[]`. Events arriving before any
 * transition lands are tagged with `phaseSeq: 0` (pre-Phase-1 — the
 * Planning phase always emits seq=1 first).
 */

import { create } from 'zustand';

export interface PhaseChatEvent {
  /** Unique within a session. Mirrors `chat/types.ChatMessage.id`. */
  readonly id: string;
  /** The phase sequence this event was buffered against. 0 = pre-Phase-1. */
  readonly phaseSeq: number;
  /** Role of the originating chat message. */
  readonly role: 'user' | 'assistant';
  /** Message content snapshot (read-only — we only filter, never edit). */
  readonly content: string;
  /** Wall-clock for display ordering only — never used for phase semantics. */
  readonly timestamp: number;
}

interface PhaseEventsState {
  /** All buffered events for the current game, oldest first. */
  events: ReadonlyArray<PhaseChatEvent>;
  /** Most recent transition `seq` we've seen. */
  currentPhaseSeq: number;
  /** GameId scoping — events for a previous game are evicted on switch. */
  gameId: string | undefined;

  /** Buffer a chat event under the current `phaseSeq`. */
  recordEvent(event: Omit<PhaseChatEvent, 'phaseSeq'>): void;
  /** Update the current `phaseSeq` from the latest `transitions[]`. */
  setCurrentPhaseSeq(seq: number): void;
  /** Switch the active gameId; flushes events from the previous game. */
  setGameId(gameId: string | undefined): void;
  /** Filter events for a given phase sequence number. */
  eventsForPhaseSeq(seq: number): ReadonlyArray<PhaseChatEvent>;
  /** Test helper — clear everything. */
  reset(): void;
}

export const usePhaseEventsStore = create<PhaseEventsState>((set, get) => ({
  events: [],
  currentPhaseSeq: 0,
  gameId: undefined,

  recordEvent(event) {
    const { currentPhaseSeq, events } = get();
    // Dedupe by id. The Workspace phase-events mirror loop iterates
    // `messages[lastMirroredCount..]` after each chat-state update;
    // when a `chat/checkpoint-received` action inserts a checkpoint
    // by timestamp *before* an already-mirrored user/assistant row,
    // every row after the insertion point shifts forward by one and
    // the next mirror tick re-reads ids that have already been
    // recorded here. Without this guard, those rows would be
    // recorded a second time under the (possibly new) current
    // `phaseSeq`, producing duplicate events that pollute the
    // click-to-expand filter. The skip-inline-checkpoint patch on
    // the mirror side handles the role guard; this id-dedupe handles
    // the index-shift fallout. Codex round-1 MED, Crucible #739
    // (C4 Issue E).
    if (events.some((e) => e.id === event.id)) return;
    const next: PhaseChatEvent = { ...event, phaseSeq: currentPhaseSeq };
    set({ events: [...events, next] });
  },

  setCurrentPhaseSeq(seq) {
    if (seq === get().currentPhaseSeq) return;
    set({ currentPhaseSeq: seq });
  },

  setGameId(gameId) {
    if (gameId === get().gameId) return;
    set({ gameId, events: [], currentPhaseSeq: 0 });
  },

  eventsForPhaseSeq(seq) {
    return get().events.filter((e) => e.phaseSeq === seq);
  },

  reset() {
    set({ events: [], currentPhaseSeq: 0, gameId: undefined });
  },
}));
